import { Outlet, Route } from 'react-router-dom';

import { USER_PERMISSIONS } from 'constants/userPermissions';
import { lazyNamedWithChunkRecovery, lazyWithChunkRecovery } from 'helper/lazyWithChunkRecovery';
import { AccessControlRoute, PrivateRoute } from 'components/routes';

import * as routes from './routes';

const SUPER_ADMIN_ONLY = [USER_PERMISSIONS.SUPER_ADMIN];

const PrivateLayout = lazyNamedWithChunkRecovery(() => import('layouts/PrivateLayout'), 'PrivateLayout');

const Home = lazyWithChunkRecovery(() => import('screens/Home'));
const OrganisationsList = lazyWithChunkRecovery(() => import('screens/OrganisationsList'));
const AccountSettings = lazyWithChunkRecovery(() => import('screens/AccountSettings'));
const NewCulturalProject = lazyWithChunkRecovery(() => import('screens/NewCulturalProject'));
const NewProjectSuccess = lazyWithChunkRecovery(() => import('screens/NewProjectSuccess'));
const CulturalProjectAdvancedSettings = lazyWithChunkRecovery(() => import('screens/CulturalProjectAdvancedSettings'));
const Dashboard = lazyWithChunkRecovery(() => import('screens/Dashboard'));
const DashboardBucket = lazyWithChunkRecovery(() => import('screens/DashboardBucket'));
const PersonalTracker = lazyWithChunkRecovery(() => import('screens/PersonalTracker'));
const NewFeedbackFramework = lazyWithChunkRecovery(() => import('screens/NewFeedbackFramework'));
const NewFeedbackSuccess = lazyWithChunkRecovery(() => import('screens/NewFeedbackSuccess'));
const ViewNominees = lazyWithChunkRecovery(() => import('screens/ViewNominees'));
const FeedBackNominees = lazyWithChunkRecovery(() => import('screens/FeedBackNominees'));
const NomineeReject = lazyWithChunkRecovery(() => import('screens/NomineeReject'));
const Report = lazyWithChunkRecovery(() => import('screens/Report'));
const TeamReport = lazyWithChunkRecovery(() => import('screens/TeamReport'));
const ProjectList = lazyWithChunkRecovery(() => import('screens/ProjectList'));
const ManageUsers = lazyWithChunkRecovery(() => import('screens/ManageUsers'));
const PersonalProjects = lazyWithChunkRecovery(() => import('screens/PersonalProjects'));
const CreateProject = lazyWithChunkRecovery(() => import('screens/CreateProject'));
const SecuritySettings = lazyWithChunkRecovery(() => import('screens/SecuritySettings'));
const ActivityLog = lazyWithChunkRecovery(() => import('screens/ActivityLog'));
const ActionTracker = lazyWithChunkRecovery(() => import('screens/ActionTracker'));

const DashboardPortfolio = lazyWithChunkRecovery(() => import('screens/DashboardPortfolio/DashboardPortfolio'));
const MasterDashboard = lazyWithChunkRecovery(() => import('screens/DashboardPortfolio/MasterDashboard'));
const MasterDashboardKeyArea = lazyWithChunkRecovery(() => import('screens/DashboardPortfolio/MasterDashboardKeyArea'));
const MasterDashboardsList = lazyWithChunkRecovery(() => import('screens/DashboardPortfolio/MasterDashboardsList'));

const ProjectJournalsOverview = lazyWithChunkRecovery(() => import('screens/ProjectJournals/ProjectJournalsOverview'));
const ProjectJournalEntries = lazyWithChunkRecovery(() => import('screens/ProjectJournals/ProjectJournalEntries'));

const PersonalJournalsOverview = lazyWithChunkRecovery(
  () => import('screens/PersonalJournals/PersonalJournalsOverview'),
);
const PersonalJournalPage = lazyWithChunkRecovery(() => import('screens/PersonalJournals/PersonalJournalPage'));
const PersonalJournalReport = lazyWithChunkRecovery(() => import('screens/PersonalJournals/PersonalJournalReport'));

const OrganisationOverviewLayout = lazyWithChunkRecovery(() => import('screens/OrganisationOverview'));
const OrganisationOverview = lazyWithChunkRecovery(() => import('screens/OrganisationOverview/OrganisationOverview'));
const CultureProjectOverview = lazyWithChunkRecovery(
  () => import('screens/OrganisationOverview/CultureProjectOverview'),
);

export const privateRoutes = (
  <Route
    element={
      <PrivateLayout>
        <Outlet />
      </PrivateLayout>
    }
  >
    <Route element={<PrivateRoute />}>
      <Route path={routes.home} element={<Home />} />
      <Route path={routes.organisationsList} element={<OrganisationsList />} />
      <Route path={routes.accountSettings} element={<AccountSettings />} />

      <Route path={`/:organisationId?/${routes.newCulturalProject}/:id?`} element={<NewCulturalProject />} />
      <Route path={`/:organisationId?/${routes.newProjectUsers}/:id`} element={<NewProjectSuccess />} />
      <Route
        path={`/:organisationId?/${routes.culturalProjectAdvancedSettings}/:id`}
        element={<CulturalProjectAdvancedSettings />}
      />
      <Route path={`/:organisationId?/${routes.dashboard}/:id`} element={<Dashboard />} />
      <Route path={`/:organisationId/${routes.dashboardBucket}/:id`} element={<DashboardBucket />} />
      <Route path={`/:organisationId/${routes.personalTracker}/:projectId`} element={<PersonalTracker />} />

      <Route path={`/:organisationId/${routes.newFeedbackFramework}/:id?`} element={<NewFeedbackFramework />} />
      <Route path={`/:organisationId/${routes.newFeedbackRecipient}/:id`} element={<NewFeedbackSuccess />} />
      <Route path={`/:organisationId/${routes.viewNominees}/:frameworkId/:userId`} element={<ViewNominees />} />
      <Route path={`${routes.feedbackNominees}/:frameworkId`} element={<FeedBackNominees />} />
      <Route path={`${routes.recipientReject}/:frameworkId/:id`} element={<NomineeReject />} />
      <Route path={`${routes.report}/:frameworkId/:id`} element={<Report />} />

      <Route path={`${routes.personalProjects}/:organisationId`} element={<PersonalProjects />} />
      <Route path={`${routes.actionTracker}/:organisationId`} element={<ActionTracker />} />

      <Route path={`${routes.dashboardPortfolio}/:organisationId`}>
        <Route index element={<DashboardPortfolio />} />
        <Route path={`${routes.masterDashboardDetails}/:masterDashboardId?`} element={<MasterDashboard />} />
        <Route path={`${routes.masterDashboardKeyArea}/:keyAreaId`} element={<MasterDashboardKeyArea />} />
        <Route path={routes.masterDashboardsList} element={<MasterDashboardsList />} />
      </Route>

      <Route path={`${routes.projectJournals}/:organisationId`}>
        <Route index element={<ProjectJournalsOverview />} />
        <Route path={`${routes.projectJournalsEntries}/:journalId`} element={<ProjectJournalEntries />} />
      </Route>

      <Route path={`${routes.personalJournals}/:organisationId`}>
        <Route index element={<PersonalJournalsOverview />} />
        <Route path=":journalId" element={<PersonalJournalPage />} />
        <Route path={`:journalId/${routes.personalJournalReport}`} element={<PersonalJournalReport />} />
      </Route>
    </Route>

    <Route element={<AccessControlRoute />}>
      <Route path={`${routes.teamReport}/:organisationId/:id`} element={<TeamReport />} />
      <Route path={`${routes.projectList}/:organisationId`} element={<ProjectList />} />
      <Route path={`${routes.manageUsers}/:organisationId?`} element={<ManageUsers />} />
      <Route path={`${routes.createProject}/:organisationId`} element={<CreateProject />} />
      <Route path={`${routes.securitySettings}/:organisationId`} element={<SecuritySettings />} />
      <Route path={`${routes.activityLog}/:organisationId?`} element={<ActivityLog />} />
    </Route>

    <Route element={<AccessControlRoute allow={SUPER_ADMIN_ONLY} />}>
      <Route path={routes.organisationOverview} element={<OrganisationOverviewLayout />}>
        <Route index element={<OrganisationOverview />} />
        <Route path={routes.cultureProjectOverview} element={<CultureProjectOverview />} />
      </Route>
    </Route>
  </Route>
);
